var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery } from "@tanstack/react-query";
import { querystring } from "api/querystringlite";
import { isSpecialError } from "api/specialErrorHandler";
import { t } from "i18next";
import { Toastr } from "setup";
import { Toast } from "toast";
import { Consts } from "vars";
export const BACKEND_API = Consts.isDev ? "http://localhost:3000" : "https://chemixapp.appspot.com";
/**
 * Base function that sets up auth, url and request params, then calls fetch.
 * This is used by the old callApi, as well as callApiForUseQuery which is to be used with useQuery.
 */
function fetchWithAuth(path, method, auth = null, request) {
    let headers = {};
    headers["Content-Type"] = "application/json";
    if (auth) {
        if (typeof auth === "string") {
            headers["Authorization"] = `Bearer ${auth}`;
        }
        else if (auth.state == "logged_in") {
            let jwtToken = auth.jwtToken;
            headers["Authorization"] = `Bearer ${jwtToken}`;
        }
    }
    let fetchOptions = {
        headers: headers,
        method: method,
    };
    // Add request data, depending on method.
    if (method == "GET") {
        // Stringify
        path += '?' + querystring.stringify(request);
    }
    else {
        fetchOptions["body"] = JSON.stringify(request);
    }
    return fetch(BACKEND_API + '/' + path, fetchOptions);
}
/**
 * Adapter to be used inside useMutation and useQuery
 */
export function callApiForUseQuery(path_1, method_1) {
    return __awaiter(this, arguments, void 0, function* (path, method, auth = null, request) {
        const response = yield fetchWithAuth(path, method, auth, request);
        if (!response.ok) {
            const errorJson = yield response.json();
            // Apparently it's possible to throw json as an error and receive it as a JSON object.
            throw errorJson;
        }
        return response.json();
    });
}
/**
 * Adapted version of useQuery
 */
export function useApiQuery(path, method, auth = null, request, options) {
    var _a;
    return useQuery({
        queryKey: [path, method, auth, request],
        queryFn: () => callApiForUseQuery(path, method, auth, request),
        enabled: (!!auth) && ((_a = options === null || options === void 0 ? void 0 : options.enabled) !== null && _a !== void 0 ? _a : true),
    });
}
/**
 * Helper method to call the API. It's a thin wrapper around fetch, adding:
 *  - type safety for request and response types
 *  - conversion of request data into stringified URL for GET methods
 */
export function callApi(path, method, auth = null, request, handlers) {
    fetchWithAuth(path, method, auth, request).then((r) => __awaiter(this, void 0, void 0, function* () {
        // Success case.
        let parsedJson = yield r.json();
        // Determine if this should be handled as an error or as a success.
        let isSuccess = r.status == 200 && !isSpecialError(parsedJson);
        if (isSuccess) {
            handlers.success(parsedJson);
        }
        else {
            // Handle error.
            let handled;
            if (isSpecialError(parsedJson) && handlers.special) {
                handled = handlers.special(parsedJson);
            }
            else if (handlers.common) {
                let errorJson = parsedJson;
                handlers.common(errorJson.error);
                handled = true;
            }
            else {
                handled = false;
            }
            if (!handled) {
                if (handlers.generic) {
                    handlers.generic();
                }
                else {
                    console.error(`API: ${path}. Unhandled error: `, parsedJson);
                    Toast.error(t('error.api_generic') + " " + Toastr.contactSupportButton(), t('error.oops'));
                }
            }
        }
        // Invoke final callback if specified.
        if (handlers.finally)
            handlers.finally();
        return parsedJson;
    }))
        .catch(e => {
        console.error(e);
        if (handlers.generic) {
            handlers.generic();
        }
        else {
            toastr.error(e, "Error making request.");
        }
        if (handlers.finally)
            handlers.finally();
    });
}
